'use client'

import React, { useState } from 'react'
import { useLanguage } from '@/lib/i18n/language-provider'
import type { WorkspaceElement, LEDProcessor, LEDPanel, PowerCabinet, WorkflowStep, DataPage, PowerPage, ImageData, WallTechnicalConfig } from '@/types/synoptique'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  BarChart3,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Cpu,
  Database,
  Group,
  Monitor,
  Settings,
  Ungroup,
  Users,
  Zap,
} from 'lucide-react'
import { toast } from 'sonner'
import { computeCabinetLoadMap } from '../utils/cabling-mutations'

// Type guards pour vérifier la présence de propriétés spécifiques
function hasMireConfig(elem: WorkspaceElement): elem is WorkspaceElement & { data: ImageData } {
  return elem.type === 'image' && elem.data && 'mireConfig' in elem.data
}

function hasBubbleConfig(elem: WorkspaceElement): elem is WorkspaceElement & { data: ImageData & { bubbleConfig: Record<string, unknown> } } {
  return elem.type === 'image' && elem.data && 'bubbleConfig' in elem.data
}

function hasSourceImageId(elem: WorkspaceElement): elem is WorkspaceElement & { data: ImageData & { sourceImageId: string } } {
  return elem.type === 'image' && elem.data && 'sourceImageId' in elem.data
}

function hasOpacity(elem: WorkspaceElement): elem is WorkspaceElement & { data: ImageData & { opacity: number } } {
  return elem.type === 'image' && elem.data && 'opacity' in elem.data && typeof elem.data.opacity === 'number'
}

interface PanelConnection {
  processorId: string
  port: number
  panelId: string
  order: number
  [key: string]: unknown
}

interface PowerConnection {
  cabinetId: string
  outlet: number
  panelId: string
  order: number
  [key: string]: unknown
}

interface MireConnection {
  processorId: string
  port: number
  imageId: string
  order: number
  gridPosition: { row: number; col: number }
  [key: string]: unknown
}

interface MirePowerConnection {
  cabinetId: string
  outlet: number
  imageId: string
  order: number
  gridPosition: { row: number; col: number }
  [key: string]: unknown
}

interface CableRoute {
  id: string
  type: 'data' | 'power'
  from: string
  to: string
  [key: string]: unknown
}

interface ElementGroup {
  name: string
  elementIds: string[]
  permanent?: boolean
}

interface SelectedItemForProperties {
  type: string
  data: Record<string, unknown>
}

interface RightPropertiesPanelProps {
  isRightPanelOpen: boolean
  setIsRightPanelOpen: (v: boolean) => void
  // Color overrides - outlet
  selectedOutlet: number | null
  selectedCabinetForConnection: string | null
  getOutletColor: (outlet: number, cabinetId?: string) => string
  outletColorOverrides: Record<string, Record<number, string>>
  setOutletColorOverrides: (fn: (prev: Record<string, Record<number, string>>) => Record<string, Record<number, string>>) => void
  // Color overrides - port
  selectedPort: number | null
  selectedProcessorForConnection: string | null
  getPortColor: (port: number, processorId?: string) => string
  portColorOverrides: Record<string, Record<number, string>>
  setPortColorOverrides: (fn: (prev: Record<string, Record<number, string>>) => Record<string, Record<number, string>>) => void
  // Selection
  selectedElements: string[]
  workspaceElements: WorkspaceElement[]
  setWorkspaceElements: (fn: (prev: WorkspaceElement[]) => WorkspaceElement[]) => void
  dataPages: DataPage[]
  powerPages: PowerPage[]
  selectedItemForProperties: SelectedItemForProperties | null
  getEffectiveSelectionCount: () => number
  formatNumber: (n: number | string) => string
  // Groups
  elementGroups: Record<string, ElementGroup>
  getGroupForElement: (id: string) => string | null
  createGroup: (ids: string[]) => void
  deleteGroup: (id: string) => void
  // Connections
  panelConnections: PanelConnection[]
  mireConnections: MireConnection[]
  powerConnections: PowerConnection[]
  mirePowerConnections: MirePowerConnection[]
  cableRoutes: CableRoute[]
  // Redondance : backupPorts[processorId][portPrincipal] = portBackup
  backupPorts: Record<string, Record<number, number>>
  // Export
  currentStep: WorkflowStep
  showCartouche: boolean
  setShowCartouche: (v: boolean) => void
  showExportProcessorLink: boolean
  setShowExportProcessorLink: (v: boolean) => void
  // State
  readOnly: boolean
  // Mires
  savedMires: Array<{ [key: string]: unknown }>
  // Wall technical config
  wallTechnicalConfig: WallTechnicalConfig
  setWallTechnicalConfig: (config: WallTechnicalConfig) => void
  setSavedMires: (fn: (prev: Array<{ [key: string]: unknown }>) => Array<{ [key: string]: unknown }>) => void
}

function hslToHex(hslStr: string): string {
  if (!hslStr.startsWith('hsl')) return hslStr
  const m = hslStr.match(/hsl\((\d+),\s*([\d.]+)%,\s*([\d.]+)%\)/)
  if (!m) return hslStr
  const h = parseInt(m[1]) / 360, s = parseFloat(m[2]) / 100, l = parseFloat(m[3]) / 100
  const a = s * Math.min(l, 1 - l)
  const f = (n: number) => { const k = (n + h * 12) % 12; return Math.round(255 * (l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1)))) }
  return `#${f(0).toString(16).padStart(2,'0')}${f(8).toString(16).padStart(2,'0')}${f(4).toString(16).padStart(2,'0')}`
}

export function RightPropertiesPanel({
  isRightPanelOpen,
  setIsRightPanelOpen,
  selectedOutlet,
  selectedCabinetForConnection,
  getOutletColor,
  outletColorOverrides,
  setOutletColorOverrides,
  selectedPort,
  selectedProcessorForConnection,
  getPortColor,
  portColorOverrides,
  setPortColorOverrides,
  selectedElements,
  workspaceElements,
  setWorkspaceElements,
  dataPages,
  powerPages,
  selectedItemForProperties,
  getEffectiveSelectionCount,
  formatNumber,
  elementGroups,
  getGroupForElement,
  createGroup,
  deleteGroup,
  panelConnections,
  mireConnections,
  powerConnections,
  mirePowerConnections,
  cableRoutes,
  backupPorts,
  currentStep,
  showCartouche,
  setShowCartouche,
  showExportProcessorLink,
  setShowExportProcessorLink,
  readOnly,
  savedMires,
  setSavedMires,
  wallTechnicalConfig,
  setWallTechnicalConfig,
}: RightPropertiesPanelProps) {
  const { t } = useLanguage()
  const [isBubbleOpen, setIsBubbleOpen] = useState(false)
  const [isLedConfigOpen, setIsLedConfigOpen] = useState(true)
  const [isStatsOpen, setIsStatsOpen] = useState(true)

  if (!isRightPanelOpen) {
    return (
      <div
        className="w-8 bg-gradient-to-l from-muted/40 to-muted/20 border-l-2 border-primary/20 hover:border-primary/40 hover:from-muted/60 hover:to-muted/40 cursor-pointer transition-all duration-300 flex flex-col items-center justify-center gap-4 relative group"
        onClick={() => setIsRightPanelOpen(true)}
        title={t("synoptic.clickToOpenRightPanel")}
      >
        {/* Indicateur visuel d'ouverture */}
        <div className="absolute right-0 top-0 bottom-0 w-1 bg-primary/30 group-hover:bg-primary/60 transition-colors"></div>

        <div className="text-xs text-muted-foreground group-hover:text-foreground font-medium -rotate-90 whitespace-nowrap transition-colors">
          {t("synoptic.properties")}
        </div>
        <ChevronLeft className="h-4 w-4 text-primary/60 group-hover:text-primary animate-pulse" />
        <div className="flex flex-col gap-2">
          <Settings className="h-3 w-3 text-muted-foreground opacity-60 group-hover:opacity-100 transition-opacity" />
          <Database className="h-3 w-3 text-muted-foreground opacity-60 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    )
  }

  // Computed values for color pickers
  const outletColor = selectedOutlet !== null && selectedCabinetForConnection
    ? hslToHex(getOutletColor(selectedOutlet, selectedCabinetForConnection))
    : null

  const portColor = selectedPort !== null && selectedProcessorForConnection
    ? hslToHex(getPortColor(selectedPort, selectedProcessorForConnection))
    : null

  // Selection data
  const selectedWorkspaceElements = workspaceElements.filter(e => selectedElements.includes(e.id))
  const selectedByType = {
    processor: selectedWorkspaceElements.find(e => e.type === 'processor'),
    panel: selectedWorkspaceElements.find(e => e.type === 'panel'),
    cabinet: selectedWorkspaceElements.find(e => e.type === 'cabinet'),
    image: selectedWorkspaceElements.find(e => e.type === 'image' && hasMireConfig(e))
  }
  const hasSelection = selectedWorkspaceElements.length > 0
  const onlyMireSelected = hasSelection && !!selectedByType.image && !selectedByType.processor && !selectedByType.panel && !selectedByType.cabinet

  // Boutons de création/dissociation de groupe — affichés aussi pour les mires seules
  const renderGroupControls = () => {
    const currentGroupId = selectedWorkspaceElements.length > 0 ? getGroupForElement(selectedWorkspaceElements[0].id) : null
    const allInSameGroup = currentGroupId && selectedWorkspaceElements.every(el => getGroupForElement(el.id) === currentGroupId)
    const shouldShowGroupControls = selectedWorkspaceElements.length > 1 || (selectedWorkspaceElements.length === 1 && currentGroupId)
    if (!shouldShowGroupControls) return null
    return (
      <div className="space-y-2">
        {allInSameGroup ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-primary" />
              <span className="font-medium">{elementGroups[currentGroupId]?.name || t('synoptic.group')}</span>
              {elementGroups[currentGroupId]?.permanent && (
                <Badge variant="secondary" className="text-xs">{t('synoptic.permanent')}</Badge>
              )}
            </div>
            {!elementGroups[currentGroupId]?.permanent && (
              <Button variant="destructive" size="sm" className="w-full" onClick={() => { if (readOnly) { toast.info(t('synoptic.share.readOnlyCabling')); return }; deleteGroup(currentGroupId) }} disabled={readOnly}>
                <Ungroup className="h-4 w-4 mr-2" />
                {t('synoptic.dissocierGroupe')}
              </Button>
            )}
          </div>
        ) : (
          <Button variant="default" size="sm" className="w-full" onClick={() => { if (readOnly) { toast.info(t('synoptic.share.readOnlyCabling')); return }; createGroup(selectedWorkspaceElements.map(el => el.id)) }} disabled={readOnly}>
            <Group className="h-4 w-4 mr-2" />
            {t('synoptic.createGroup')}
          </Button>
        )}
        <Separator />
      </div>
    )
  }

  return (
    <div className="w-80 border-l bg-card/50 backdrop-blur-sm overflow-y-auto transition-all duration-300 relative">
      <div className="p-3 space-y-4">

        {/* Couleur de la prise sélectionnée (Power) */}
        {outletColor && selectedOutlet !== null && selectedCabinetForConnection && (
          <Card>
            <CardContent className="py-3 space-y-2">
              <h5 className="text-sm font-medium flex items-center gap-2">
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: outletColor }} />
                Couleur de la prise {selectedOutlet}
              </h5>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={outletColor}
                  onChange={(e) => {
                    if (readOnly) { toast.info(t('synoptic.share.readOnlyCabling')); return }
                    setOutletColorOverrides(prev => ({
                      ...prev,
                      [selectedCabinetForConnection]: {
                        ...(prev[selectedCabinetForConnection] || {}),
                        [selectedOutlet]: e.target.value
                      }
                    }))
                  }}
                  className={`w-10 h-10 rounded border p-0.5 bg-background ${readOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                  title={`Personnaliser la couleur de la prise ${selectedOutlet}`}
                  disabled={readOnly}
                />
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground">Prise PC{selectedOutlet}</p>
                  <p className="text-xs text-muted-foreground font-mono">{outletColor.toUpperCase()}</p>
                </div>
                {outletColorOverrides[selectedCabinetForConnection]?.[selectedOutlet] && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-xs text-muted-foreground"
                    onClick={() => {
                      if (readOnly) { toast.info(t('synoptic.share.readOnlyCabling')); return }
                      setOutletColorOverrides(prev => {
                        const next = { ...prev }
                        if (next[selectedCabinetForConnection]) {
                          next[selectedCabinetForConnection] = { ...next[selectedCabinetForConnection] }
                          delete next[selectedCabinetForConnection][selectedOutlet]
                        }
                        return next
                      })
                    }}
                    title={t('title.resetColor')}
                    disabled={readOnly}
                  >
                    ↺ Reset
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Informations sélection - Sidebar item properties */}
        {!hasSelection && selectedItemForProperties && (() => {
          const itemByType = {
            processor: selectedItemForProperties.type === 'processor' ? { data: selectedItemForProperties.data } : null,
            panel: selectedItemForProperties.type === 'panel' ? { data: selectedItemForProperties.data } : null,
            cabinet: selectedItemForProperties.type === 'cabinet' ? { data: selectedItemForProperties.data } : null
          }

          return (
            <Card>
              <CardHeader className="pb-2 pt-3">
                <div
                  className="flex items-center justify-between gap-2 cursor-pointer hover:bg-muted/50 rounded-md p-1 -m-1 transition-colors"
                  onClick={() => setIsRightPanelOpen(false)}
                  title={t('synoptic.clickToOpenRightPanel')}
                >
                  <CardTitle className="text-lg">{t('synoptic.properties')}</CardTitle>
                  <ChevronRight className="h-4 w-4 text-muted-foreground opacity-60 hover:opacity-100 transition-opacity" />
                </div>
              </CardHeader>
              <CardContent className="space-y-4 py-3">
                {itemByType.processor && (() => {
                  const processorData = itemByType.processor.data as unknown as LEDProcessor
                  return (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Cpu className="h-4 w-4 text-primary" />
                        <h4 className="font-medium">{processorData.name}</h4>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span>{t('synoptic.brand')}</span><span className="font-medium">{processorData.brand}</span></div>
                        <div className="flex justify-between"><span>{t('synoptic.model')}</span><span className="font-medium">{processorData.model}</span></div>
                        <div className="flex justify-between"><span>{t('synoptic.outputPorts')}</span><span className="font-medium">{formatNumber(processorData.outputPorts)}</span></div>
                        <div className="flex justify-between"><span>{t('synoptic.totalPixels')}</span><span className="font-medium">{formatNumber(processorData.totalOutputPixels)}</span></div>
                        <Separator className="my-2" />
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-primary">{t('synoptic.videoInputs')}</span>
                            <Badge variant="secondary">{formatNumber(processorData.videoInputsCount || 'N/A')}</Badge>
                          </div>
                          {processorData.videoInputs && processorData.videoInputs !== 'N/A' && (
                            <div className="pl-2 space-y-1 text-xs">
                              {processorData.videoInputs.split(',').map((input, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                  <div className="w-1.5 h-1.5 rounded-full bg-primary/60" />
                                  <span className="text-muted-foreground">{input.trim()}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <Separator className="my-2" />
                        <div className="flex justify-between"><span>{t('synoptic.consumption')}</span><span className="font-medium">{formatNumber(processorData.powerConsumption)} W</span></div>
                      </div>
                    </div>
                  )
                })()}
                {itemByType.panel && (() => {
                  const panelData = itemByType.panel.data as unknown as LEDPanel
                  const SpecRow = ({ label, value }: { label: string; value?: string | number | null }) =>
                    value ? <div className="flex justify-between gap-2"><span className="text-muted-foreground shrink-0">{label}</span><span className="font-medium text-right">{value}</span></div> : null
                  return (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Monitor className="h-4 w-4 text-primary" />
                        <div>
                          <h4 className="font-medium">{panelData.name}</h4>
                          {panelData.series && (
                            <p className="text-xs text-muted-foreground">{panelData.brand} · {panelData.series}{panelData.seriesStatus === 'out_of_production' ? ' · hors prod.' : ''}</p>
                          )}
                        </div>
                      </div>
                      <div className="space-y-1.5 text-sm">
                        <p className="text-[10px] font-semibold uppercase text-muted-foreground/60 tracking-wider pt-1">Panneau</p>
                        <SpecRow label="Marque" value={panelData.brand} />
                        <SpecRow label="Pixel Pitch" value={panelData.pixelPitch > 0 ? `${panelData.pixelPitch} mm` : panelData.panelResolution} />
                        <SpecRow label="Taille (L×H×P)" value={panelData.panelDiagonal ? panelData.panelDiagonal.replace(/panel_size/,'') : (panelData.dimensions.width > 0 ? `${panelData.dimensions.width}×${panelData.dimensions.height} mm` : undefined)} />
                        {panelData.dimensions.width === 0 && !panelData.panelDiagonal && <SpecRow label="Taille" value={panelData.panelResolution} />}
                        <SpecRow label="Diagonale" value={panelData.panelDiagonal} />
                        <SpecRow label="Résolution" value={panelData.resolution.width > 0 ? `${panelData.resolution.width}×${panelData.resolution.height} px` : panelData.panelResolution} />
                        <SpecRow label="Densité pixels" value={panelData.pixelDensity} />
                        <SpecRow label="Surface" value={panelData.panelArea} />
                        <SpecRow label="Poids" value={panelData.panelWeight} />
                        <SpecRow label="IP" value={panelData.ipRating} />
                        <SpecRow label="Certifications" value={panelData.certifications} />
                        {(panelData.powerMax || panelData.powerConsumption > 0) && (
                          <p className="text-[10px] font-semibold uppercase text-muted-foreground/60 tracking-wider pt-2">Alimentation</p>
                        )}
                        <SpecRow label="Conso max" value={panelData.powerMax || (panelData.powerConsumption > 0 ? `${panelData.powerConsumption} W` : undefined)} />
                        <SpecRow label="Conso moy." value={panelData.powerAvg} />
                        <SpecRow label="Tension" value={panelData.lineVoltage} />
                        {(panelData.moduleSize || panelData.modulesPerPanel) && (
                          <p className="text-[10px] font-semibold uppercase text-muted-foreground/60 tracking-wider pt-2">Module</p>
                        )}
                        <SpecRow label="Taille module" value={panelData.moduleSize} />
                        <SpecRow label="Modules/Panneau" value={panelData.modulesPerPanel} />
                        <SpecRow label="LEDs/Module" value={panelData.ledsPerModule} />
                        <p className="text-[10px] font-semibold uppercase text-muted-foreground/60 tracking-wider pt-2">Optique</p>
                        <SpecRow label="Luminosité" value={panelData.brightness > 0 ? `${panelData.brightness.toLocaleString()} nits` : undefined} />
                        <SpecRow label="Type LED" value={panelData.ledType} />
                        <SpecRow label="Niveaux gris" value={panelData.grayScale} />
                        <SpecRow label="Contraste" value={panelData.contrastRatio} />
                        <SpecRow label="Angle de vue" value={panelData.viewingAngle} />
                        <SpecRow label="Durée de vie" value={panelData.ledLife} />
                        <SpecRow label="Taux de rafraîch." value={panelData.ledRefreshRate} />
                        <SpecRow label="Temp. couleur" value={panelData.colorTemperature} />
                        {(panelData.maxHangingRows || panelData.maxStackingRows || panelData.receivingCard) && (
                          <p className="text-[10px] font-semibold uppercase text-muted-foreground/60 tracking-wider pt-2">Installation</p>
                        )}
                        <SpecRow label="Accrochage max" value={panelData.maxHangingRows ? `${panelData.maxHangingRows} rangées` : undefined} />
                        <SpecRow label="Empilement max" value={panelData.maxStackingRows ? `${panelData.maxStackingRows} rangées` : undefined} />
                        <SpecRow label="Carte récept." value={panelData.receivingCard} />
                        <SpecRow label="Humidité" value={panelData.humidity} />
                        {panelData.notableFeatures && (
                          <div className="pt-1">
                            <p className="text-[10px] font-semibold uppercase text-muted-foreground/60 tracking-wider">Caractéristiques</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{panelData.notableFeatures}</p>
                          </div>
                        )}
                        {panelData.sourceUrl && (
                          <a
                            href={panelData.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-primary/60 hover:text-primary pt-1"
                          >
                            Source LEDWallCentral →
                          </a>
                        )}
                      </div>
                    </div>
                  )
                })()}
                {itemByType.cabinet && (() => {
                  const cabinetData = itemByType.cabinet.data as unknown as PowerCabinet
                  return (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Zap className="h-4 w-4 text-red-500" />
                        <h4 className="font-medium">{cabinetData.name}</h4>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span>{t('synoptic.brand')}</span><span className="font-medium">{cabinetData.brand}</span></div>
                        <div className="flex justify-between"><span>{t('synoptic.cabinetType')}</span><span className="font-medium">{cabinetData.type}</span></div>
                        <div className="flex justify-between"><span>{t('synoptic.maxPower')}</span><span className="font-medium">{(cabinetData.maxPower / 1000).toFixed(1)} kW</span></div>
                        {(() => {
                          // L'item sélectionné n'a pas d'id — retrouver l'élément via la référence data
                          const cabinetEl = workspaceElements.find(e => e.type === 'cabinet' && e.data === selectedItemForProperties.data)
                          if (!cabinetEl) return null
                          const loads = computeCabinetLoadMap(workspaceElements, powerConnections, mirePowerConnections, cableRoutes)
                          const totalW = loads.total.get(cabinetEl.id) || 0
                          const overloaded = cabinetData.maxPower > 0 && totalW > cabinetData.maxPower
                          return (
                            <div className="flex justify-between">
                              <span>{t('synoptic.cabinetLoad')}</span>
                              <span className={`font-medium ${overloaded ? 'text-red-500' : ''}`}>
                                {(totalW / 1000).toFixed(2)} kW{overloaded ? ` — ${t('synoptic.cabinetOverload')}` : ''}
                              </span>
                            </div>
                          )
                        })()}
                        <div className="flex justify-between"><span>{t("synoptic.outletsCount")}:</span><span className="font-medium">{cabinetData.outlets}</span></div>
                        <div className="flex justify-between"><span>{t('synoptic.voltage')}</span><span className="font-medium">{cabinetData.voltage}</span></div>
                        <div className="flex justify-between"><span>{t('synoptic.dimensions')}</span><span className="font-medium">{cabinetData.dimensions.width}×{cabinetData.dimensions.height}mm</span></div>
                      </div>
                    </div>
                  )
                })()}
              </CardContent>
            </Card>
          )
        })()}

        {/* Informations sélection - Workspace elements */}
        {hasSelection && !onlyMireSelected && (
          <Card>
            <CardHeader className="pb-2 pt-3">
              <div
                className="flex items-center justify-between gap-2 cursor-pointer hover:bg-muted/50 rounded-md p-1 -m-1 transition-colors"
                onClick={() => setIsRightPanelOpen(false)}
                title={t('synoptic.clickToHideRightPanel')}
              >
                <CardTitle className="text-lg">
                  {getEffectiveSelectionCount() === 1 ? t('synoptic.properties') : t('synoptic.selectedItems', { count: getEffectiveSelectionCount() })}
                </CardTitle>
                <ChevronRight className="h-4 w-4 text-muted-foreground opacity-60 hover:opacity-100 transition-opacity" />
              </div>
            </CardHeader>
            <CardContent className="space-y-4 py-3">
              {/* Bouton pour créer/gérer un groupe */}
              {renderGroupControls()}

              {/* Afficher les processeurs sélectionnés */}
              {selectedByType.processor && (() => {
                const processorData = selectedByType.processor.data as LEDProcessor
                const processorElementId = selectedByType.processor.id
                const count = selectedWorkspaceElements.filter(e => e.type === 'processor').length
                return (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Cpu className="h-4 w-4 text-primary" />
                      <h4 className="font-medium">
                        {processorData.customName || processorData.name}
                        {count > 1 && <Badge variant="secondary" className="ml-2 text-xs">×{count}</Badge>}
                      </h4>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="processor-custom-name" className="text-xs">{t('synoptic.processorCustomName')}</Label>
                      <Input
                        id="processor-custom-name"
                        value={processorData.customName || ''}
                        placeholder={`${processorData.brand} ${processorData.model}`}
                        onChange={(e) => {
                          if (readOnly) { toast.info(t('synoptic.share.readOnlyCabling')); return }
                          const newName = e.target.value
                          setWorkspaceElements(prev => prev.map(el =>
                            el.id === processorElementId && el.type === 'processor'
                              ? { ...el, data: { ...el.data, customName: newName || undefined } }
                              : el
                          ))
                        }}
                        className="h-7 text-xs"
                        disabled={readOnly}
                      />
                      <p className="text-[10px] text-muted-foreground">{t('synoptic.processorCustomNameDesc')}</p>
                    </div>
                    <Separator className="my-1" />
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span>{t('synoptic.brand')}</span><span className="font-medium">{processorData.brand}</span></div>
                      <div className="flex justify-between"><span>{t('synoptic.model')}</span><span className="font-medium">{processorData.model}</span></div>
                      <div className="flex justify-between"><span>{t('synoptic.outputPorts')}</span><span className="font-medium">{formatNumber(processorData.outputPorts)}</span></div>
                      <div className="flex justify-between"><span>{t('synoptic.totalPixels')}</span><span className="font-medium">{formatNumber(processorData.totalOutputPixels)}</span></div>
                      <Separator className="my-2" />
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-primary">{t('synoptic.videoInputs')}</span>
                          <Badge variant="secondary">{formatNumber(processorData.videoInputsCount || 'N/A')}</Badge>
                        </div>
                        {processorData.videoInputs && processorData.videoInputs !== 'N/A' && (
                          <div className="pl-2 space-y-1 text-xs">
                            {processorData.videoInputs.split(',').map((input, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-primary/60" />
                                <span className="text-muted-foreground">{input.trim()}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <Separator className="my-2" />
                      <div className="flex justify-between"><span>{t('synoptic.consumption')}</span><span className="font-medium">{formatNumber(processorData.powerConsumption)} W</span></div>
                    </div>
                  </div>
                )
              })()}

              {/* Afficher les panneaux sélectionnés */}
              {selectedByType.panel && (() => {
                const panelData = selectedByType.panel.data as LEDPanel
                const count = selectedWorkspaceElements.filter(e => e.type === 'panel').length
                const panelOpacity = hasOpacity(selectedByType.panel) ? selectedByType.panel.data.opacity || 1 : 1
                const SpecRow = ({ label, value }: { label: string; value?: string | number | null }) =>
                  value ? <div className="flex justify-between gap-2 text-sm"><span className="text-muted-foreground shrink-0">{label}</span><span className="font-medium text-right">{value}</span></div> : null
                return (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Monitor className="h-4 w-4 text-primary" />
                      <div>
                        <h4 className="font-medium">
                          {panelData.name}
                          {count > 1 && <Badge variant="secondary" className="ml-2 text-xs">×{count}</Badge>}
                        </h4>
                        {panelData.series && (
                          <p className="text-xs text-muted-foreground">{panelData.brand} · {panelData.series}{panelData.seriesStatus === 'out_of_production' ? ' · hors prod.' : ''}</p>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <p className="text-[10px] font-semibold uppercase text-muted-foreground/60 tracking-wider">Panneau</p>
                      <SpecRow label="Marque" value={panelData.brand} />
                      <SpecRow label="Pixel Pitch" value={panelData.pixelPitch > 0 ? `${panelData.pixelPitch} mm` : undefined} />
                      <SpecRow label="Taille" value={panelData.dimensions.width > 0 ? `${panelData.dimensions.width}×${panelData.dimensions.height} mm` : undefined} />
                      <SpecRow label="Diagonale" value={panelData.panelDiagonal} />
                      <SpecRow label="Résolution" value={panelData.resolution.width > 0 ? `${panelData.resolution.width}×${panelData.resolution.height} px` : panelData.panelResolution} />
                      <SpecRow label="Densité" value={panelData.pixelDensity} />
                      <SpecRow label="Surface" value={panelData.panelArea} />
                      <SpecRow label="Poids" value={panelData.panelWeight} />
                      <SpecRow label="IP" value={panelData.ipRating} />
                      <SpecRow label="Certif." value={panelData.certifications} />
                      {(panelData.powerMax || panelData.powerConsumption > 0) && (
                        <p className="text-[10px] font-semibold uppercase text-muted-foreground/60 tracking-wider pt-1">Alimentation</p>
                      )}
                      <SpecRow label="Conso max" value={panelData.powerMax || (panelData.powerConsumption > 0 ? `${panelData.powerConsumption} W` : undefined)} />
                      <SpecRow label="Conso moy." value={panelData.powerAvg} />
                      <SpecRow label="Tension" value={panelData.lineVoltage} />
                      {(panelData.moduleSize || panelData.modulesPerPanel) && (
                        <p className="text-[10px] font-semibold uppercase text-muted-foreground/60 tracking-wider pt-1">Module</p>
                      )}
                      <SpecRow label="Taille module" value={panelData.moduleSize} />
                      <SpecRow label="Modules/Panel" value={panelData.modulesPerPanel} />
                      <SpecRow label="LEDs/Module" value={panelData.ledsPerModule} />
                      <p className="text-[10px] font-semibold uppercase text-muted-foreground/60 tracking-wider pt-1">Optique</p>
                      <SpecRow label="Luminosité" value={panelData.brightness > 0 ? `${panelData.brightness.toLocaleString()} nits` : undefined} />
                      <SpecRow label="Type LED" value={panelData.ledType} />
                      <SpecRow label="Niveaux gris" value={panelData.grayScale} />
                      <SpecRow label="Contraste" value={panelData.contrastRatio} />
                      <SpecRow label="Angle vue" value={panelData.viewingAngle} />
                      <SpecRow label="Durée de vie" value={panelData.ledLife} />
                      <SpecRow label="Refresh" value={panelData.ledRefreshRate} />
                      <SpecRow label="Temp. couleur" value={panelData.colorTemperature} />
                      {(panelData.maxHangingRows || panelData.maxStackingRows || panelData.receivingCard) && (
                        <p className="text-[10px] font-semibold uppercase text-muted-foreground/60 tracking-wider pt-1">Installation</p>
                      )}
                      <SpecRow label="Accrochage max" value={panelData.maxHangingRows ? `${panelData.maxHangingRows} rangées` : undefined} />
                      <SpecRow label="Empilement max" value={panelData.maxStackingRows ? `${panelData.maxStackingRows} rangées` : undefined} />
                      <SpecRow label="Carte récept." value={panelData.receivingCard} />
                      {panelData.notableFeatures && (
                        <p className="text-xs text-muted-foreground pt-1 border-t border-border/50">{panelData.notableFeatures}</p>
                      )}
                      {panelData.sourceUrl && (
                        <a href={panelData.sourceUrl} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-primary/60 hover:text-primary pt-1">
                          Source LEDWallCentral →
                        </a>
                      )}
                    </div>
                    {/* Contrôle d'opacité */}
                    <div className="space-y-2 pt-2 border-t">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="panel-opacity">{t('synoptic.opacity')}</Label>
                        <span className="text-sm text-muted-foreground">{Math.round((panelOpacity as number) * 100)}%</span>
                      </div>
                      <input
                        id="panel-opacity"
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={panelOpacity as number}
                        onChange={(e) => {
                          if (readOnly) { toast.info(t('synoptic.share.readOnlyCabling')); return }
                          const newOpacity = parseFloat(e.target.value)
                          setWorkspaceElements(prev => prev.map(el =>
                            selectedElements.includes(el.id) && el.type === 'panel'
                              ? { ...el, data: { ...el.data, opacity: newOpacity } }
                              : el
                          ))
                        }}
                        className={`w-full h-2 bg-muted rounded-lg appearance-none ${readOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                        disabled={readOnly}
                      />
                    </div>
                  </div>
                )
              })()}

              {/* Afficher les armoires sélectionnées */}
              {selectedByType.cabinet && (() => {
                const cabinetData = selectedByType.cabinet.data as PowerCabinet
                const count = selectedWorkspaceElements.filter(e => e.type === 'cabinet').length
                return (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-red-500" />
                      <h4 className="font-medium">
                        {cabinetData.name}
                        {count > 1 && <Badge variant="secondary" className="ml-2 text-xs">×{count}</Badge>}
                      </h4>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span>{t('synoptic.brand')}</span><span className="font-medium">{cabinetData.brand}</span></div>
                      <div className="flex justify-between"><span>{t('synoptic.cabinetType')}</span><span className="font-medium">{cabinetData.type}</span></div>
                      <div className="flex justify-between"><span>{t('synoptic.maxPower')}</span><span className="font-medium">{(cabinetData.maxPower / 1000).toFixed(1)} kW</span></div>
                      <div className="flex justify-between"><span>{t('synoptic.outletsCount')}</span><span className="font-medium">{cabinetData.outlets}</span></div>
                      <div className="flex justify-between"><span>{t('synoptic.voltage')}</span><span className="font-medium">{cabinetData.voltage}</span></div>
                      <div className="flex justify-between"><span>{t('synoptic.dimensions')}</span><span className="font-medium">{cabinetData.dimensions.width}×{cabinetData.dimensions.height}mm</span></div>
                    </div>
                  </div>
                )
              })()}

              {/* Afficher les mires sélectionnées avec configuration de bulle */}
              {!onlyMireSelected && selectedByType.image && (() => {
                const mireElement = selectedByType.image
                
                // Fonction utilitaire pour valider les types de manière sécurisée
                const safeGetBubbleConfig = (elem: typeof mireElement): Record<string, unknown> => {
                  if (hasBubbleConfig(elem)) {
                    return elem.data.bubbleConfig || {}
                  }
                  return {}
                }
                
                const safeGetLinkedPanel = (elem: typeof mireElement): LEDPanel | undefined => {
                  if (elem.type === 'image' && elem.data && 'linkedPanel' in elem.data) {
                    return elem.data.linkedPanel as LEDPanel
                  }
                  return undefined
                }
                
                const bubbleConfig = safeGetBubbleConfig(mireElement)
                const mireName = mireElement.data?.name?.split(' - ')[0] || 'Mire'
                const linkedPanel = safeGetLinkedPanel(mireElement)
                const bgColor = (bubbleConfig.bgColor as string) || 'white'
                const textColor = (bubbleConfig.textColor as string) || 'black'
                const bubbleWidth = (bubbleConfig.width as number) || 220
                const bubbleHeight = (bubbleConfig.height as number) || (linkedPanel ? 52 : 40)
                const titleFontSize = (bubbleConfig.titleFontSize as number) || 11
                const infoFontSize = (bubbleConfig.infoFontSize as number) || 9
                                
                const updateBubbleConfig = (key: string, value: string | number | boolean) => {
                  if (readOnly) { toast.info(t('synoptic.share.readOnlyCabling')); return }
                  
                  // Validation robuste pour les entrées numériques
                  const numericKeys = ['width', 'height', 'titleFontSize', 'infoFontSize']
                  if (typeof value === 'string' && numericKeys.includes(key)) {
                    try {
                      const numValue = parseInt(value, 10)
                      if (isNaN(numValue) || numValue < 0) {
                        toast.error(t('synoptic.toast.invalidNumericValue'))
                        return // Ignorer les valeurs invalides
                      }
                      value = numValue
                    } catch (error) {
                      toast.error(t('synoptic.toast.parsingError'))
                      return
                    }
                  }
                  
                  // Validation des couleurs hexadécimales
                  if (typeof value === 'string' && (key === 'bgColor' || key === 'textColor')) {
                    if (!/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(value)) {
                      toast.error(t('synoptic.toast.invalidHexColor'))
                      return
                    }
                  }
                  
                  setWorkspaceElements(prev => {
                    return prev.map(el => {
                      // Mise à jour de l'élément principal
                      if (el.id === mireElement.id) {
                        const currentBubbleConfig = hasBubbleConfig(el) ? el.data.bubbleConfig : {}
                        return { ...el, data: { ...el.data, bubbleConfig: { ...currentBubbleConfig, [key]: value } } }
                      }
                      
                      // Mise à jour des éléments liés si c'est un titre
                      if (key === 'title' && el.id !== mireElement.id && el.type === 'image' && hasMireConfig(el)) {
                        const sourceId = hasSourceImageId(mireElement) ? mireElement.data.sourceImageId || mireElement.id : mireElement.id
                        const elSourceId = hasSourceImageId(el) ? el.data.sourceImageId || el.id : el.id
                        const sourceImageIdMatch = hasSourceImageId(el) ? el.data.sourceImageId === mireElement.id : false
                        if (elSourceId === sourceId || sourceImageIdMatch) {
                          const currentBubbleConfig = hasBubbleConfig(el) ? el.data.bubbleConfig : {}
                          return { ...el, data: { ...el.data, bubbleConfig: { ...currentBubbleConfig, title: value } } }
                        }
                      }
                      
                      return el
                    })
                  })
                }

                return (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Monitor className="h-4 w-4 text-primary" />
                      <h4 className="font-medium">{mireName}</h4>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between pb-2 border-b">
                        <Label htmlFor="bubble-visible" className="text-xs font-medium cursor-pointer">
                          {t('synoptic.bubble.visible')}
                        </Label>
                        <Switch
                          id="bubble-visible"
                          checked={(bubbleConfig.visible as boolean) ?? true}
                          onCheckedChange={(checked) => updateBubbleConfig('visible', checked)}
                          disabled={readOnly}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('synoptic.bubble.title')}</Label>
                        <Input value={(bubbleConfig.title as string) || mireName} onChange={(e) => updateBubbleConfig('title', e.target.value)} className="h-7 text-xs" disabled={readOnly} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">{t('synoptic.bubble.bgColor')}</Label>
                          <input type="color" value={bgColor} onChange={(e) => updateBubbleConfig('bgColor', e.target.value)} className="w-full h-7 rounded border p-0.5 bg-background cursor-pointer" disabled={readOnly} />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">{t('synoptic.bubble.textColor')}</Label>
                          <input type="color" value={textColor} onChange={(e) => updateBubbleConfig('textColor', e.target.value)} className="w-full h-7 rounded border p-0.5 bg-background cursor-pointer" disabled={readOnly} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">{t('synoptic.bubble.width')}</Label>
                          <Input type="number" min={100} max={400} value={bubbleWidth} onChange={(e) => updateBubbleConfig('width', parseInt(e.target.value) || 220)} className="h-7 text-xs" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">{t('synoptic.bubble.height')}</Label>
                          <Input type="number" min={30} max={100} value={bubbleHeight} onChange={(e) => updateBubbleConfig('height', parseInt(e.target.value) || 40)} className="h-7 text-xs" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">{t('synoptic.bubble.titleFont')}</Label>
                          <Input type="number" min={8} max={20} value={titleFontSize} onChange={(e) => updateBubbleConfig('titleFontSize', parseInt(e.target.value) || 11)} className="h-7 text-xs" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">{t('synoptic.bubble.infoFont')}</Label>
                          <Input type="number" min={6} max={16} value={infoFontSize} onChange={(e) => updateBubbleConfig('infoFontSize', parseInt(e.target.value) || 9)} className="h-7 text-xs" />
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t">
                        <Label htmlFor="count-power-consumption" className="text-xs font-medium cursor-pointer">
                          {t('synoptic.bubble.countPowerConsumption')}
                        </Label>
                        <Switch
                          id="count-power-consumption"
                          checked={(bubbleConfig.countPowerConsumption as boolean) ?? true}
                          onCheckedChange={(checked) => updateBubbleConfig('countPowerConsumption', checked)}
                          disabled={readOnly}
                        />
                      </div>
                      <Button variant="outline" size="sm" className="w-full h-7 text-xs" disabled={readOnly} onClick={() => {
                        if (readOnly) { toast.info(t('synoptic.share.readOnlyCabling')); return }
                        setWorkspaceElements(prev => prev.map(el => {
                          if (el.id === mireElement.id) {
                            const { bubbleConfig: _bc, ...restData } = el.data || {}
                            return { ...el, data: restData }
                          }
                          return el
                        }))
                      }}>
                        {t('synoptic.bubble.reset')}
                      </Button>
                    </div>
                  </div>
                )
              })()}
            </CardContent>
          </Card>
        )}

        {/* Mire Properties + Configuration Mur LED - Merged Card */}
        {onlyMireSelected && selectedByType.image && (() => {
          const mireElement = selectedByType.image

          const safeGetBubbleConfig = (elem: typeof mireElement): Record<string, unknown> => {
            if (hasBubbleConfig(elem)) return elem.data.bubbleConfig || {}
            return {}
          }
          const safeGetLinkedPanel = (elem: typeof mireElement): LEDPanel | undefined => {
            if (elem.type === 'image' && elem.data && 'linkedPanel' in elem.data) return elem.data.linkedPanel as LEDPanel
            return undefined
          }

          const bubbleConfig = safeGetBubbleConfig(mireElement)
          const mireName = (mireElement.data?.name as string)?.split(' - ')[0] || 'Mire'
          const linkedPanel = safeGetLinkedPanel(mireElement)
          const bgColor = (bubbleConfig.bgColor as string) || 'white'
          const textColor = (bubbleConfig.textColor as string) || 'black'
          const bubbleWidth = (bubbleConfig.width as number) || 220
          const bubbleHeight = (bubbleConfig.height as number) || (linkedPanel ? 52 : 40)
          const titleFontSize = (bubbleConfig.titleFontSize as number) || 11
          const infoFontSize = (bubbleConfig.infoFontSize as number) || 9

          const updateBubbleConfig = (key: string, value: string | number | boolean) => {
            if (readOnly) { toast.info(t('synoptic.share.readOnlyCabling')); return }
            const numericKeys = ['width', 'height', 'titleFontSize', 'infoFontSize']
            if (typeof value === 'string' && numericKeys.includes(key)) {
              const numValue = parseInt(value, 10)
              if (isNaN(numValue) || numValue < 0) { toast.error(t('synoptic.toast.invalidNumericValue')); return }
              value = numValue
            }
            if (typeof value === 'string' && (key === 'bgColor' || key === 'textColor')) {
              if (!/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(value)) { toast.error(t('synoptic.toast.invalidHexColor')); return }
            }
            setWorkspaceElements(prev => prev.map(el => {
              if (el.id === mireElement.id) {
                const currentBubbleConfig = hasBubbleConfig(el) ? el.data.bubbleConfig : {}
                return { ...el, data: { ...el.data, bubbleConfig: { ...currentBubbleConfig, [key]: value } } }
              }
              if (key === 'title' && el.id !== mireElement.id && el.type === 'image' && hasMireConfig(el)) {
                const sourceId = hasSourceImageId(mireElement) ? mireElement.data.sourceImageId || mireElement.id : mireElement.id
                const elSourceId = hasSourceImageId(el) ? el.data.sourceImageId || el.id : el.id
                const sourceImageIdMatch = hasSourceImageId(el) ? el.data.sourceImageId === mireElement.id : false
                if (elSourceId === sourceId || sourceImageIdMatch) {
                  const currentBubbleConfig = hasBubbleConfig(el) ? el.data.bubbleConfig : {}
                  return { ...el, data: { ...el.data, bubbleConfig: { ...currentBubbleConfig, title: value } } }
                }
              }
              return el
            }))
          }

          return (
            <Card key="merged-mire-card">
              <CardHeader className="pb-2 pt-3">
                <div
                  className="flex items-center justify-between gap-2 cursor-pointer hover:bg-muted/50 rounded-md p-1 -m-1 transition-colors"
                  onClick={() => setIsRightPanelOpen(false)}
                  title={t('synoptic.clickToHideRightPanel')}
                >
                  <div className="flex items-center gap-2">
                    <Monitor className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg">{mireName} properties</CardTitle>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground opacity-60 hover:opacity-100 transition-opacity" />
                </div>
              </CardHeader>
              <CardContent className="space-y-2 py-3">

                {/* Bouton pour créer/gérer un groupe (mires uniquement sélectionnées) */}
                {renderGroupControls()}

                {/* Section Bulle - collapsible */}
                <div
                  className="flex items-center justify-between cursor-pointer hover:bg-muted/40 rounded-md px-2 py-1.5 -mx-2 transition-colors select-none"
                  onClick={() => setIsBubbleOpen(v => !v)}
                >
                  <span className="text-sm font-semibold">Bulle</span>
                  <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isBubbleOpen ? 'rotate-180' : ''}`} />
                </div>
                {isBubbleOpen && (
                  <div className="space-y-2 pt-1">
                    <div className="flex items-center justify-between pb-2 border-b">
                      <Label htmlFor="bubble-visible-single" className="text-xs font-medium cursor-pointer">
                        {t('synoptic.bubble.visible')}
                      </Label>
                      <Switch
                        id="bubble-visible-single"
                        checked={(bubbleConfig.visible as boolean) ?? true}
                        onCheckedChange={(checked) => updateBubbleConfig('visible', checked)}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t('synoptic.bubble.title')}</Label>
                      <Input value={(bubbleConfig.title as string) || mireName} onChange={(e) => updateBubbleConfig('title', e.target.value)} className="h-7 text-xs" disabled={readOnly} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">{t('synoptic.bubble.bgColor')}</Label>
                        <input type="color" value={bgColor} onChange={(e) => updateBubbleConfig('bgColor', e.target.value)} className="w-full h-7 rounded border p-0.5 bg-background cursor-pointer" disabled={readOnly} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('synoptic.bubble.textColor')}</Label>
                        <input type="color" value={textColor} onChange={(e) => updateBubbleConfig('textColor', e.target.value)} className="w-full h-7 rounded border p-0.5 bg-background cursor-pointer" disabled={readOnly} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">{t('synoptic.bubble.width')}</Label>
                        <Input type="number" min={100} max={400} value={bubbleWidth} onChange={(e) => updateBubbleConfig('width', parseInt(e.target.value) || 220)} className="h-7 text-xs" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('synoptic.bubble.height')}</Label>
                        <Input type="number" min={30} max={100} value={bubbleHeight} onChange={(e) => updateBubbleConfig('height', parseInt(e.target.value) || 40)} className="h-7 text-xs" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">{t('synoptic.bubble.titleFont')}</Label>
                        <Input type="number" min={8} max={20} value={titleFontSize} onChange={(e) => updateBubbleConfig('titleFontSize', parseInt(e.target.value) || 11)} className="h-7 text-xs" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('synoptic.bubble.infoFont')}</Label>
                        <Input type="number" min={6} max={16} value={infoFontSize} onChange={(e) => updateBubbleConfig('infoFontSize', parseInt(e.target.value) || 9)} className="h-7 text-xs" />
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="w-full h-7 text-xs" disabled={readOnly} onClick={() => {
                      if (readOnly) { toast.info(t('synoptic.share.readOnlyCabling')); return }
                      setWorkspaceElements(prev => prev.map(el => {
                        if (el.id === mireElement.id) {
                          const { bubbleConfig: _bc, ...restData } = el.data || {}
                          return { ...el, data: restData }
                        }
                        return el
                      }))
                    }}>
                      {t('synoptic.bubble.reset')}
                    </Button>
                  </div>
                )}

                {/* Section Configuration Mur LED - collapsible */}
                {(currentStep === 'data' || currentStep === 'cabling') && (
                  <>
                    <Separator />
                    <div
                      className="flex items-center justify-between cursor-pointer hover:bg-muted/40 rounded-md px-2 py-1.5 -mx-2 transition-colors select-none"
                      onClick={() => setIsLedConfigOpen(v => !v)}
                    >
                      <div className="flex items-center gap-2">
                        <Settings className="h-4 w-4 text-primary" />
                        <span className="text-sm font-semibold">Configuration Mur LED</span>
                      </div>
                      <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isLedConfigOpen ? 'rotate-180' : ''}`} />
                    </div>
                    {isLedConfigOpen && (
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Profondeur de couleur</Label>
                          <Select
                            value={wallTechnicalConfig.colorDepth.toString()}
                            onValueChange={(value) => setWallTechnicalConfig({ ...wallTechnicalConfig, colorDepth: parseInt(value) as 8 | 10 | 12 | 16 })}
                            disabled={readOnly}
                          >
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="8">8 bits (Standard)</SelectItem>
                              <SelectItem value="10">10 bits (Haute qualité)</SelectItem>
                              <SelectItem value="12">12 bits (Ultra HD)</SelectItem>
                              <SelectItem value="16">16 bits (HDR)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Taux de rafraîchissement</Label>
                          <Select
                            value={wallTechnicalConfig.refreshRate.toString()}
                            onValueChange={(value) => {
                              const r = parseInt(value, 10)
                              if (r === 50 || r === 60) setWallTechnicalConfig({ ...wallTechnicalConfig, refreshRate: r as 50 | 60 })
                            }}
                            disabled={readOnly}
                          >
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="50">50 Hz</SelectItem>
                              <SelectItem value="60">60 Hz</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium cursor-pointer">HDR activé</Label>
                          <Switch
                            checked={wallTechnicalConfig.hdrEnabled}
                            onCheckedChange={(checked) => setWallTechnicalConfig({ ...wallTechnicalConfig, hdrEnabled: checked })}
                            disabled={readOnly}
                          />
                        </div>
                        {wallTechnicalConfig.panelResolution && (
                          <div className="pt-2 border-t space-y-1">
                            <div className="text-xs text-muted-foreground">Résolution panneau</div>
                            <div className="text-sm font-medium">{wallTechnicalConfig.panelResolution.width} × {wallTechnicalConfig.panelResolution.height} px</div>
                          </div>
                        )}
                        {wallTechnicalConfig.panelWeight && (
                          <div className="space-y-1">
                            <div className="text-xs text-muted-foreground">Poids panneau</div>
                            <div className="text-sm font-medium">{wallTechnicalConfig.panelWeight} kg</div>
                          </div>
                        )}
                        {wallTechnicalConfig.totalPanels && (
                          <div className="space-y-1">
                            <div className="text-xs text-muted-foreground">Nombre de panneaux</div>
                            <div className="text-sm font-medium">{wallTechnicalConfig.totalPanels}</div>
                          </div>
                        )}
                        {wallTechnicalConfig.totalPixels && (
                          <div className="space-y-1">
                            <div className="text-xs text-muted-foreground">Total pixels</div>
                            <div className="text-sm font-medium">{formatNumber(wallTechnicalConfig.totalPixels)}</div>
                          </div>
                        )}
                        {wallTechnicalConfig.calculatedPortCapacity && (
                          <div className="pt-2 border-t space-y-1">
                            <div className="text-xs text-muted-foreground">Capacité port RJ45</div>
                            <div className="text-sm font-bold text-primary">{formatNumber(wallTechnicalConfig.calculatedPortCapacity)} pixels/port</div>
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground pt-2 border-t">
                          Ces paramètres affectent le calcul de la charge des ports RJ45 pour le câblage en daisy chain.
                        </p>
                      </div>
                    )}
                  </>
                )}

                {/* Count Power Consumption - toujours visible en bas */}
                <div className="flex items-center justify-between pt-2 border-t">
                  <Label htmlFor="count-power-consumption-merged" className="text-xs font-medium cursor-pointer">
                    {t('synoptic.bubble.countPowerConsumption')}
                  </Label>
                  <Switch
                    id="count-power-consumption-merged"
                    checked={(bubbleConfig.countPowerConsumption as boolean) ?? true}
                    onCheckedChange={(checked) => updateBubbleConfig('countPowerConsumption', checked)}
                    disabled={readOnly}
                  />
                </div>
              </CardContent>
            </Card>
          )
        })()}

        {/* Options d'export */}
        {currentStep === 'export' && (
          <Card>
            <CardHeader className="pb-2 pt-3">
              <div
                className="flex items-center justify-between gap-2 cursor-pointer hover:bg-muted/50 rounded-md p-1 -m-1 transition-colors"
                onClick={() => setIsRightPanelOpen(false)}
                title={t('synoptic.clickToHideRightPanel')}
              >
                <CardTitle className="text-lg">{t('synoptic.exportOptions')}</CardTitle>
                <ChevronRight className="h-4 w-4 text-muted-foreground opacity-60 hover:opacity-100 transition-opacity" />
              </div>
            </CardHeader>
            <CardContent className="space-y-3 py-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="show-cartouche" className="text-sm font-medium cursor-pointer">
                  {t('synoptic.showCartouche')}
                </Label>
                <Switch
                  id="show-cartouche"
                  checked={showCartouche}
                  onCheckedChange={setShowCartouche}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('synoptic.showCartoucheDesc')}
              </p>
              <div className="flex items-center justify-between pt-2 border-t">
                <Label htmlFor="show-export-proc-link" className="text-sm font-medium cursor-pointer">
                  {t('synoptic.showProcessorLink')}
                </Label>
                <Switch
                  id="show-export-proc-link"
                  checked={showExportProcessorLink}
                  onCheckedChange={setShowExportProcessorLink}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('synoptic.showProcessorLinkDesc')}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Configuration technique du mur LED (standalone, hidden when mire selected) */}
        {(currentStep === 'data' || currentStep === 'cabling') && !onlyMireSelected && (
          <Card>
            <CardHeader className="pb-2 pt-3">
              <div
                className="flex items-center justify-between gap-2 cursor-pointer hover:bg-muted/50 rounded-md p-1 -m-1 transition-colors"
                onClick={() => setIsRightPanelOpen(false)}
                title={t('synoptic.clickToHideRightPanel')}
              >
                <div className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg">Configuration Mur LED</CardTitle>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground opacity-60 hover:opacity-100 transition-opacity" />
              </div>
            </CardHeader>
            <CardContent className="space-y-3 py-3">
              <div className="space-y-2">
                <Label htmlFor="color-depth" className="text-sm font-medium">Profondeur de couleur</Label>
                <Select
                  value={wallTechnicalConfig.colorDepth.toString()}
                  onValueChange={(value) => {
                    const newConfig = { ...wallTechnicalConfig, colorDepth: parseInt(value) as 8 | 10 | 12 | 16 }
                    setWallTechnicalConfig(newConfig)
                  }}
                  disabled={readOnly}
                >
                  <SelectTrigger id="color-depth" className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="8">8 bits (Standard)</SelectItem>
                    <SelectItem value="10">10 bits (Haute qualité)</SelectItem>
                    <SelectItem value="12">12 bits (Ultra HD)</SelectItem>
                    <SelectItem value="16">16 bits (HDR)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="refresh-rate" className="text-sm font-medium">Taux de rafraîchissement</Label>
                <Select
                  value={wallTechnicalConfig.refreshRate.toString()}
                  onValueChange={(value) => {
                    const parsedRate = parseInt(value, 10)
                    if (parsedRate === 50 || parsedRate === 60) {
                      const newConfig = { ...wallTechnicalConfig, refreshRate: parsedRate as 50 | 60 }
                      setWallTechnicalConfig(newConfig)
                    }
                  }}
                  disabled={readOnly}
                >
                  <SelectTrigger id="refresh-rate" className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50 Hz</SelectItem>
                    <SelectItem value="60">60 Hz</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="hdr-enabled" className="text-sm font-medium cursor-pointer">HDR activé</Label>
                <Switch
                  id="hdr-enabled"
                  checked={wallTechnicalConfig.hdrEnabled}
                  onCheckedChange={(checked) => {
                    const newConfig = { ...wallTechnicalConfig, hdrEnabled: checked }
                    setWallTechnicalConfig(newConfig)
                  }}
                  disabled={readOnly}
                />
              </div>

              {wallTechnicalConfig.panelResolution && (
                <div className="pt-2 border-t space-y-1">
                  <div className="text-xs text-muted-foreground">Résolution panneau</div>
                  <div className="text-sm font-medium">{wallTechnicalConfig.panelResolution.width} × {wallTechnicalConfig.panelResolution.height} px</div>
                </div>
              )}

              {wallTechnicalConfig.panelWeight && (
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground">Poids panneau</div>
                  <div className="text-sm font-medium">{wallTechnicalConfig.panelWeight} kg</div>
                </div>
              )}

              {wallTechnicalConfig.totalPanels && (
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground">Nombre de panneaux</div>
                  <div className="text-sm font-medium">{wallTechnicalConfig.totalPanels}</div>
                </div>
              )}

              {wallTechnicalConfig.totalPixels && (
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground">Total pixels</div>
                  <div className="text-sm font-medium">{formatNumber(wallTechnicalConfig.totalPixels)}</div>
                </div>
              )}

              {wallTechnicalConfig.calculatedPortCapacity && (
                <div className="pt-2 border-t space-y-1">
                  <div className="text-xs text-muted-foreground">Capacité port RJ45</div>
                  <div className="text-sm font-bold text-primary">{formatNumber(wallTechnicalConfig.calculatedPortCapacity)} pixels/port</div>
                </div>
              )}

              <p className="text-xs text-muted-foreground pt-2 border-t">
                Ces paramètres affectent le calcul de la charge des ports RJ45 pour le câblage en daisy chain.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Project Statistics */}
        <Card>
          <CardHeader className="pb-2 pt-3">
            <div
              className="flex items-center justify-between gap-2 cursor-pointer hover:bg-muted/50 rounded-md p-1 -m-1 transition-colors"
              onClick={() => setIsStatsOpen(v => !v)}
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">{t("synoptic.stats.title")}</CardTitle>
              </div>
              <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isStatsOpen ? 'rotate-180' : ''}`} />
            </div>
          </CardHeader>
          {isStatsOpen && <CardContent className="py-3">
            {(() => {
              const mires = workspaceElements.filter(el => el.type === 'image' && el.data?.mireConfig)
              const allPages = [...dataPages, ...powerPages]
              
              // Fonction optimisée pour calculer toutes les statistiques en un seul passage
              const calculateAllStats = (pages: typeof allPages) => {
                const stats = {
                  processors: 0,
                  cabinets: 0,
                  powerCables: 0,
                  totalPanelsFromMires: 0,
                  allMireElements: [] as typeof mires
                }

                // Calculer le nombre de panneaux à partir des mires importées (savedMires)
                savedMires.forEach((mire: { [key: string]: unknown }) => {
                  const ledWallMode = mire.ledWallMode as { columns: number; rows: number } | undefined
                  if (ledWallMode) {
                    stats.totalPanelsFromMires += (ledWallMode.columns || 0) * (ledWallMode.rows || 0)
                  }
                })
                
                pages.forEach(page => {
                  if (!page.workspaceElements) return
                  
                  page.workspaceElements.forEach(elem => {
                    if (elem.type === 'processor') stats.processors++
                    else if (elem.type === 'cabinet') stats.cabinets++
                    else if (elem.type === 'image' && hasMireConfig(elem)) {
                      stats.allMireElements.push(elem)
                    }
                  })
                  
                  if (page.cableRoutes) {
                    stats.powerCables += page.cableRoutes.filter(c => c.type === 'power').length
                  }
                })
                
                return stats
              }
              
              const { processors, cabinets, powerCables: powerCablesAllPages, totalPanelsFromMires, allMireElements } = calculateAllStats(allPages)
              
              const dataCables = cableRoutes.filter(c => c.type === 'data').length
              const dataLinks = panelConnections.length + mireConnections.length
              // First Data = 1 câble par chaîne (processeur → 1er panneau)
              // + 1 câble de redondance par chaîne dont le port a un backup
              // (backup → dernier panneau). Ex : 8 chaînes + 8 backups = 16.
              const firstChains = new Set(
                [...panelConnections, ...mireConnections]
                  .filter(c => c.order === 1)
                  .map(c => `${c.processorId}|${c.port}`)
              )
              let firstData = firstChains.size
              firstChains.forEach(key => {
                const sep = key.lastIndexOf('|')
                const procId = key.slice(0, sep)
                const port = Number(key.slice(sep + 1))
                if (backupPorts[procId]?.[port] !== undefined) firstData++
              })
              const firstPower = new Set([...powerConnections, ...mirePowerConnections].filter(c => c.order === 1).map(c => `${c.cabinetId}-${c.outlet}`)).size
              
              // Fonction utilitaire pour parser la consommation d'un panneau
              const parsePanelConsumption = (linkedPanel: LEDPanel | undefined): number => {
                if (!linkedPanel) return 0
                
                const powerMaxStr = typeof linkedPanel.powerMax === 'string' ? linkedPanel.powerMax : undefined
                const powerAvgStr = typeof linkedPanel.powerAvg === 'string' ? linkedPanel.powerAvg : undefined
                const powerCons = typeof linkedPanel.powerConsumption === 'number' ? linkedPanel.powerConsumption : undefined
                
                let panelConsumption = 0
                
                // Essayer powerMax d'abord
                if (powerMaxStr) {
                  // Extraire le premier nombre valide (ex: "100W" -> 100, "100-200W" -> 100)
                  const match = powerMaxStr.match(/(\d+(?:\.\d+)?)/)
                  if (match) {
                    const numValue = parseFloat(match[1])
                    if (!isNaN(numValue) && numValue > 0) {
                      panelConsumption = numValue
                    }
                  }
                }
                
                // Si powerMax n'a pas donné de résultat, essayer powerAvg
                if (panelConsumption === 0 && powerAvgStr) {
                  const match = powerAvgStr.match(/(\d+(?:\.\d+)?)/)
                  if (match) {
                    const numValue = parseFloat(match[1])
                    if (!isNaN(numValue) && numValue > 0) {
                      panelConsumption = numValue
                    }
                  }
                }
                
                // Si les strings n'ont pas donné de résultat, utiliser powerConsumption
                if (panelConsumption === 0 && powerCons !== undefined) {
                  panelConsumption = powerCons
                }
                
                return panelConsumption > 0 ? panelConsumption : 0
              }
              
              // Fonction pour calculer la consommation d'une mire
              const calculateMireConsumption = (elem: WorkspaceElement): { consumption: number; details?: { name: string; consumption: number; panelCount: number; panelConsumption: number } } => {
                if (elem.type !== 'image' || !elem.data?.mireConfig) {
                  return { consumption: 0, details: undefined }
                }
                const bubbleConfig = (elem.data?.bubbleConfig || {}) as Record<string, unknown>
                const countPowerConsumption = bubbleConfig.countPowerConsumption !== false
                if (!countPowerConsumption) {
                  return { consumption: 0, details: undefined }
                }
                const mireConfig = elem.data.mireConfig as Record<string, unknown> | undefined
                const ledWallMode = mireConfig?.ledWallMode as Record<string, unknown> | undefined
                const columns = (ledWallMode?.columns as number) || 0
                const rows = (ledWallMode?.rows as number) || 0
                const panelCount = columns * rows
                const mireName = (bubbleConfig.title as string) || (elem.data.name as string)?.split(' - ')[0] || 'Mire'

                // Priorité 1 : totalPower déjà calculé lors du lien panneau↔mire
                const totalPowerFromConfig = mireConfig?.totalPower as number | undefined
                if (totalPowerFromConfig && totalPowerFromConfig > 0 && panelCount > 0) {
                  const panelConsumption = Math.round(totalPowerFromConfig / panelCount)
                  return {
                    consumption: totalPowerFromConfig,
                    details: { name: mireName, consumption: totalPowerFromConfig, panelCount, panelConsumption }
                  }
                }

                // Priorité 2 : parser linkedPanel
                const linkedPanel = elem.data.linkedPanel as LEDPanel | undefined
                if (linkedPanel && panelCount > 0) {
                  const panelConsumption = parsePanelConsumption(linkedPanel)
                  if (panelConsumption > 0) {
                    return {
                      consumption: panelConsumption * panelCount,
                      details: { name: mireName, consumption: panelConsumption * panelCount, panelCount, panelConsumption }
                    }
                  }
                }
                return { consumption: 0, details: undefined }
              }
              
              // Calculer la consommation des mires depuis page courante + toutes pages
              // Utiliser directement les pages pour accéder au linkedPanel (comme le PDF)
              const allMiresFromPages: WorkspaceElement[] = []
              allPages.forEach(page => {
                if (page.workspaceElements) {
                  page.workspaceElements.forEach(elem => {
                    if (elem.type === 'image' && elem.data?.mireConfig) {
                      allMiresFromPages.push(elem)
                    }
                  })
                }
              })
              // Préférer les mires avec linkedPanel lors de la déduplication
              const uniqueMiresMap = new Map<string, WorkspaceElement>()
              allMiresFromPages.forEach(mire => {
                const existing = uniqueMiresMap.get(mire.id)
                const hasLinkedPanel = !!mire.data?.linkedPanel
                const existingHasLinkedPanel = !!existing?.data?.linkedPanel
                // Garder la version avec linkedPanel, ou la première si aucune n'en a
                if (!existing || (hasLinkedPanel && !existingHasLinkedPanel)) {
                  uniqueMiresMap.set(mire.id, mire)
                }
              })
              const uniqueMires = Array.from(uniqueMiresMap.values())
              
              const mireConsumptionResults = uniqueMires.map(mireElement => ({
                element: mireElement,
                result: calculateMireConsumption(mireElement)
              }))

              const mireConsumption = mireConsumptionResults.reduce((sum, { result }) => sum + result.consumption, 0)

              // Calculer les détails des mires à partir des résultats précalculés
              const mireDetails: Array<{ name: string; consumption: number; panelCount: number; panelConsumption: number }> = []
              mireConsumptionResults.forEach(({ result }) => {
                if ((result as { details?: { name: string; consumption: number; panelCount: number; panelConsumption: number } }).details) {
                  mireDetails.push((result as { details: { name: string; consumption: number; panelCount: number; panelConsumption: number } }).details)
                }
              })
              
              // Calculer la consommation totale (équipements + mires)
              const totalConsumption = allPages.reduce((sum, page) => {
                return sum + page.workspaceElements.reduce((pageSum, elem) => {
                  // Pour toutes les mires, ne pas compter ici car elles sont déjà gérées dans mireConsumption
                  if (elem.type === 'image' && elem.data?.mireConfig) {
                    return pageSum
                  }
                  const consumption = 'powerConsumption' in elem.data ? elem.data.powerConsumption : 0
                  return pageSum + (typeof consumption === 'number' ? consumption : 0)
                }, 0)
              }, 0) + mireConsumption
              
              return (
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="col-span-2 flex justify-between p-2 bg-primary/10 rounded border border-primary/30"><span className="text-primary">{t('synoptic.stats.totalPanels')}</span><span className="font-bold text-primary">{totalPanelsFromMires}</span></div>
                  <div className="flex justify-between p-2 bg-muted/50 rounded"><span className="text-muted-foreground">{t("synoptic.stats.processors")}</span><span className="font-bold">{processors}</span></div>
                  <div className="flex justify-between p-2 bg-muted/50 rounded"><span className="text-muted-foreground">{t("synoptic.stats.cabinets")}</span><span className="font-bold">{cabinets}</span></div>
                  <div className="flex justify-between p-2 bg-muted/50 rounded"><span className="text-muted-foreground">{t("synoptic.stats.dataLinks")}</span><span className="font-bold text-blue-400">{dataLinks}</span></div>
                  <div className="flex justify-between p-2 bg-muted/50 rounded"><span className="text-muted-foreground">{t("synoptic.stats.powerCables")}</span><span className="font-bold text-red-500">{powerCablesAllPages}</span></div>
                  <div className="flex justify-between p-2 bg-blue-500/10 rounded border border-blue-500/30"><span className="text-blue-500">{t("synoptic.stats.firstData")}</span><span className="font-bold text-blue-500">{firstData}</span></div>
                  <div className="flex justify-between p-2 bg-red-500/10 rounded border border-red-500/30"><span className="text-red-500">{t("synoptic.stats.firstPower")}</span><span className="font-bold text-red-500">{firstPower}</span></div>
                  <div className="col-span-2 flex flex-col gap-2 bg-purple-500/10 rounded border border-purple-500/30 p-2">
                    <div className="flex justify-between">
                      <span className="text-purple-600 font-medium">{t('synoptic.mireConsumption')}</span>
                      <span className="font-bold text-purple-600">{mireConsumption} W</span>
                    </div>
                    {mireDetails.length > 0 && (
                      <div className="space-y-1 pt-1 border-t border-purple-500/20">
                        {mireDetails.map((mire, index) => (
                          <div key={`mire-detail-${index}-${mire.name}-${mire.panelCount}-${mire.panelConsumption}`} className="flex justify-between text-[10px] text-purple-600/80">
                            <span>{mire.name} ({mire.panelCount} × {mire.panelConsumption}W)</span>
                            <span className="font-medium">{mire.consumption} W</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="col-span-2 flex justify-between p-2 bg-yellow-500/10 rounded border border-yellow-500/30"><span className="text-yellow-600">{t('synoptic.estimatedConsumption')}</span><span className="font-bold text-yellow-600">{totalConsumption} W</span></div>
                </div>
              )
            })()}
          </CardContent>}
        </Card>

      </div>
    </div>
  )
}
